import React, { useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import {
    PageTitle,
    Loading,
    Button,
    Modal,
    Tooltip
} from '../root/components/common'
import { NavLink, useLocation, withRouter } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import * as actions from './actions'
import { IconButton } from '@material-ui/core'
import { TableVirtualized } from '../root/components/common/table-virtualized/table'
import { UserPermissions } from '../../services/permissions'
import { DialogOpener } from '../root/components/common/dialog-opener'
import { ShareLocationModal } from './components/share-location-modal'
import DeleteIcon from '@material-ui/icons/DeleteOutline'
import { mdiAccountArrowLeftOutline, mdiAccountCircleOutline } from '@mdi/js'
import MdiIcon from '@mdi/react'
import { deleteUserFromLocation } from './api/locations-api'
import { getSagasMsgs } from '../root/selectors'
import { enqueueSnackbar } from '../root/actions'
import { getRoleName } from 'web/src/utils'

const useStyles = makeStyles(theme => ({
    wrapper: {
        height: 'calc(100% - 125px)'
    },
    blockIconButton: {
        color: theme.primary
    },
    modalButtons: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: 15
    },
    userNameLink: {
        ...theme.link
    }
}))

function LocatioShareInfo(props) {
    const classes = useStyles()
    const locationId = props.match.params.locationId
    const isNewLocation = locationId === 'new'
    const { isLoading, locale, theme } = useSelector(state => state.main)
    const { sharedUsersWithEntity, location } = useSelector(
        state => state.locations
    )
    const loggedInUser = useSelector(state => state.main.user)
    const broserLocation = useLocation()
    const dispatch = useDispatch()
    const sagasMsgs = useSelector(getSagasMsgs)

    const shareLocationModalOpen = new URLSearchParams(
        broserLocation.search
    ).get('isShare')

    const permissions = {
        updateLocation: UserPermissions.location(
            location && location.customer_id
        ).update,
        share: UserPermissions.location(loggedInUser.original_customer_id)
            .update,
        delete: UserPermissions.location(loggedInUser.original_customer_id)
            .update
    }

    const columns = [
        {
            key: 'user-icon',
            label: null,
            sortable: false,
            dataType: 'custom',
            width: 0.4,
            render: ({ rowData, cellData }) => {
                if (rowData.is_external) {
                    return (
                        <Tooltip
                            hover
                            placement="top"
                            text={locale.userManagement.externalUser}
                        >
                            <MdiIcon
                                path={mdiAccountArrowLeftOutline}
                                size={'20px'}
                                color={theme.quaternary}
                            />
                        </Tooltip>
                    )
                }
                return (
                    <Tooltip
                        hover
                        placement="top"
                        text={locale.userManagement.internalUser}
                    >
                        <MdiIcon
                            path={mdiAccountCircleOutline}
                            size={'20px'}
                            color={theme.primary}
                        />
                    </Tooltip>
                )
            }
        },
        {
            key: 'name',
            label: locale.common.name,
            // sortable: true,
            dataType: 'string',
            width: 1.5,
            render: ({ rowData, cellData }) =>
                UserPermissions.isUserMgmtGranted() ? (
                    <NavLink
                        className={classes.userNameLink}
                        to={`/user-management/users/${rowData.id}`}
                    >
                        {cellData}
                    </NavLink>
                ) : (
                    cellData
                )
        },
        {
            key: 'email',
            label: locale.common.email,
            // sortable: true,
            width: 1.5,
            dataType: 'string'
        },
        {
            key: 'role_code',
            label: locale.userManagement.roleName,
            // sortable: true,
            width: 1.5,
            dataType: 'string',
            render: ({ rowData, cellData }) => {
                return getRoleName(
                    {
                        name: rowData.role_name,
                        code: rowData.role_code
                    },
                    locale
                )
            }
        },
        {
            key: 'actions',
            label: locale.userManagement.actions,
            sortable: false,
            dataType: 'custom',
            width: 1.5,
            alignCenter: true,
            render: ({ rowData, cellData }) => {
                const canDelete =
                    permissions.updateLocation &&
                    UserPermissions.isOriginalEntity(location?.customer_id)
                return (
                    <div className={classes.actions}>
                        <Tooltip
                            hover
                            placement="top"
                            text={
                                canDelete
                                    ? ''
                                    : locale.userManagement
                                          .youCanNotDeleteThisUser
                            }
                        >
                            <IconButton
                                size="small"
                                disabled={!canDelete}
                                onClick={() => onDeleteClick(rowData.id)}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Tooltip>
                    </div>
                )
            }
        }
    ]

    const onDeleteClick = id => {
        deleteUserFromLocation(locationId, { users: [id] })
            .then(() => {
                fetchUsers()
                dispatch(
                    enqueueSnackbar({
                        message: sagasMsgs.revokeSharingRes,
                        options: {
                            variant: 'success'
                        }
                    })
                )
            })
            .catch(e => {
                dispatch(
                    enqueueSnackbar({
                        message: sagasMsgs.revokeSharingEx,
                        options: {
                            variant: 'error'
                        }
                    })
                )
            })
    }

    const fetchUsers = () =>
        dispatch(
            actions.locationAssignedUsersRequest(locationId, {
                is_assigned: true
            })
        )

    useEffect(() => {
        fetchUsers()

        return () => {
            dispatch(actions.locationAssignedUsersSuccess(null))
        }
    }, [locationId])

    return (
        <div className={classes.wrapper}>
            <PageTitle title={locale.locations.shareInfo}>
                {!isNewLocation &&
                    location &&
                    UserPermissions.isOriginalEntity(location.customer_id) &&
                    permissions.share && (
                        <DialogOpener
                            initialOpen={shareLocationModalOpen}
                            dialog={(handleClose, modalProps) => {
                                return modalProps.open ? (
                                    <ShareLocationModal
                                        onClose={() => {
                                            handleClose()
                                            fetchUsers()
                                        }}
                                        modalProps={modalProps}
                                        locationId={locationId}
                                    />
                                ) : null
                            }}
                        >
                            {handleOpen => (
                                <Button onClick={handleOpen}>
                                    {locale.locations.add}
                                </Button>
                            )}
                        </DialogOpener>
                    )}
            </PageTitle>

            {isLoading && <Loading />}

            {!isLoading &&
                (!sharedUsersWithEntity ||
                    sharedUsersWithEntity.length === 0) && (
                    <div>{locale.locations.locationNotShared}</div>
                )}

            {!isLoading &&
                sharedUsersWithEntity &&
                sharedUsersWithEntity.length > 0 && (
                    <TableVirtualized
                        data={sharedUsersWithEntity}
                        isLoading={isLoading}
                        columns={columns}
                    />
                )}
        </div>
    )
}

export default withRouter(LocatioShareInfo)
